import theme from '../../theme';
import { useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';
import useFredData from './useFredData';
import buildChartConfig from './chartConfig';


const statCardStyle = {
  background: theme.bgDark,
  borderRadius: '4px',
  padding: '0.75rem 1.25rem',
  minWidth: '140px',
  textAlign: 'center',
  border: '1px solid rgba(184, 149, 90, 0.2)',
};

const cycleCardStyle = (accent) => ({
  background: theme.bgCard,
  borderRadius: '4px',
  border: '1px solid rgba(184, 149, 90, 0.2)',
  borderTop: `3px solid ${accent}`,
  padding: '1.25rem 1.5rem',
});

const chartSectionStyle = {
  marginBottom: '2rem',
};

const cycles = [
  {
    label: 'Dot-com Boom & Bust',
    years: '1998 – 2003',
    accent: '#d68a4c',
    points: [
      'Boom (1998–2000): Rates held between 4.75%–6.5% as the Fed attempted to cool speculative tech investment. Too little, too late.',
      'Bust (2001–2003): After the Nasdaq collapsed, the Fed slashed rates aggressively from 6.5% down to 1%, the lowest in 45 years at the time.',
      'The cheap credit that followed didn\'t reflate tech. It inflated housing instead. The seeds of the next crisis were planted here.'
    ]
  },
  {
    label: 'GFC Housing Boom & Bust',
    years: '2004 – 2009',
    accent: theme.error,
    points: [
      'Boom (2004–2006): Rates rose gradually from 1% to 5.25%, but the damage was already done. Years of cheap credit had fuelled a housing bubble that rate hikes couldn\'t deflate cleanly.',
      'Bust (2007–2009): As mortgage defaults cascaded, the Fed cut rates back to near-zero (0–0.25%) in an emergency response to the worst financial crisis since the Great Depression.',
      'The Austrian lesson: the boom was not caused by the rate hike. It was caused by the preceding suppression. The bust was the inevitable correction.'
    ]
  },
  {
    label: 'Post-GFC: A Decade Near Zero',
    years: '2009 – 2021',
    accent: '#5b8dbe',
    points: [
      'Post-GFC (2009–2015): Rates held near zero for six years, an unprecedented duration. Capital was cheap, asset prices inflated, and productive investment was crowded out by financial engineering.',
      'Pre-COVID tightening (2015–2018): A cautious, gradual tightening cycle brought rates to ~2.5%, still well below historical norms. The Fed blinked and cut again in 2019 before completing the cycle.',
      'COVID (2020): Rates slashed back to zero almost overnight. The fastest and most aggressive easing in Fed history.'
    ]
  },
  {
    label: 'Post-COVID Tightening',
    years: '2022 – Present',
    accent: theme.gold,
    points: [
      'The Fed held rates near zero through 2021 while inflation accelerated, then hiked at the fastest pace since the 1980s, from 0% to 5.25–5.5% in just 16 months.',
      'This is textbook Austrian: years of suppressed rates created malinvestment across tech, crypto, and real estate. The tightening exposed it.',
      'The average Fed Funds Rate over the last 30 years is approximately 2.4%, a figure dragged down by prolonged near-zero periods that have no precedent in pre-2008 history.'
    ]
  }
];

const FredChart = () => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const { data, loading, error } = useFredData();

  const current = data.length ? data[data.length - 1].value : null;
  const high = data.length ? Math.max(...data.map(d => d.value)) : null;
  const low = data.length ? Math.min(...data.map(d => d.value)) : null;

  useEffect(() => {
    if (!data.length) return;
    if (chartRef.current) chartRef.current.destroy();
    chartRef.current = new Chart(canvasRef.current, buildChartConfig(data));
    return () => chartRef.current?.destroy();
  }, [data]);

  if (loading) return <p style={{ textAlign: 'center', marginTop: '2rem', color: theme.textMuted }}>Loading...</p>;
  if (error) return <p style={{ textAlign: 'center', color: theme.error }}>Error: {error}</p>;

  return (
    <div>
      <div style={chartSectionStyle}>
        <div style={{ display: 'flex', gap: '12px', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          <div style={statCardStyle}>
            <div style={{ fontSize: '13px', color: theme.textMuted, marginBottom: '4px' }}>Current rate</div>
            <div style={{ fontSize: '24px', fontWeight: '500', color: theme.gold }}>{current?.toFixed(2)}%</div>
          </div>
          <div style={statCardStyle}>
            <div style={{ fontSize: '13px', color: theme.textMuted, marginBottom: '4px' }}>30-year high</div>
            <div style={{ fontSize: '24px', fontWeight: '500', color: theme.gold }}>{high?.toFixed(2)}%</div>
          </div>
          <div style={statCardStyle}>
            <div style={{ fontSize: '13px', color: theme.textMuted, marginBottom: '4px' }}>30-year low</div>
            <div style={{ fontSize: '24px', fontWeight: '500', color: theme.gold }}>{low?.toFixed(2)}%</div>
          </div>
          <div style={statCardStyle}>
            <div style={{ fontSize: '13px', color: theme.textMuted, marginBottom: '4px' }}>30-year average</div>
            <div style={{ fontSize: '24px', fontWeight: '500', color: theme.gold }}>~2.4%</div>
          </div>
        </div>

        <p style={{ fontSize: '13px', color: theme.textMuted, marginBottom: '1rem' }}>
          Fund Funds Rate: Last 30 years. Source: FRED, Federal Reserve Bank of St. Louis
        </p>

        <div style={{ position: 'relative', height: '480px' }}>
          <canvas ref={canvasRef}></canvas>
        </div>
      </div>

      <div style={{ borderTop: '1px solid rgba(184, 149, 90, 0.2)', paddingTop: '2rem' }}>
        <h3 style={{ color: theme.gold, marginBottom: '1.5rem', fontSize: '17px', fontFamily: "'EB Garamond', Georgia, serif" }}>Reading the Chart: An Austrian Lens</h3>
        <div className="fred-cycles-grid">
          {cycles.map((cycle, i) => (
            <div key={i} style={cycleCardStyle(cycle.accent)}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', marginBottom: '0.75rem' }}>
                <strong style={{ fontSize: '15px', color: cycle.accent }}>{cycle.label}</strong>
                <span style={{ fontSize: '13px', color: theme.textMuted }}>{cycle.years}</span>
              </div>
              <ul style={{ fontSize: '14px', lineHeight: '1.9', color: theme.textPrimary, paddingLeft: '1.25rem', margin: 0 }}>
                {cycle.points.map((point, j) => (
                  <li key={j}>{point}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FredChart;